import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Slider } from "@/components/ui/slider";
import { ArrowRight, ShieldCheck, Users } from "lucide-react";
import {
  businessLoanCost, formatGBPCompact, formatGBP2,
  amountToPos, posToAmount, SLIDER_STEPS,
  DEFAULT_INDICATIVE_APR,
} from "@/lib/api";

const QUICK_AMOUNTS = [
  { v: 25_000, label: "£25k" },
  { v: 100_000, label: "£100k" },
  { v: 500_000, label: "£500k", popular: true },
  { v: 1_000_000, label: "£1M" },
  { v: 5_000_000, label: "£5M" },
  { v: 25_000_000, label: "£25M" },
];

export default function EligibilityForm() {
  const [amount, setAmount] = useState(500_000);
  const [term, setTerm] = useState(36);
  const nav = useNavigate();
  const { monthly } = useMemo(() => businessLoanCost(amount, term), [amount, term]);

  const submit = () => {
    const p = new URLSearchParams({ amount: String(amount), term: String(term) });
    nav(`/apply?${p.toString()}`);
  };

  return (
    <div className="relative bg-white rounded-[20px] border border-line shadow-form p-6 md:p-7" data-testid="loan-calculator">
      <div className="text-center mb-6">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] font-bold text-navy/50 mb-1">How much does your business need?</div>
      </div>

      {/* Quick-pick chips */}
      <div className="grid grid-cols-6 gap-2 mb-6">
        {QUICK_AMOUNTS.map((q) => (
          <button
            key={q.v}
            onClick={() => setAmount(q.v)}
            data-testid={`amount-chip-${q.v}`}
            className={`relative rounded-xl py-3 text-center text-[13px] font-extrabold border transition-all
              ${amount === q.v
                ? "bg-navy text-white border-navy shadow-card"
                : "bg-white border-line text-navy hover:border-navy/30 hover:bg-sage"}`}
          >
            {q.popular && (
              <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-emerald text-white text-[8.5px] uppercase font-extrabold tracking-wider">
                Popular
              </span>
            )}
            {q.label}
          </button>
        ))}
      </div>

      {/* Live amount display */}
      <div className="bg-sage/60 rounded-xl p-5 mb-5">
        <div className="flex items-baseline justify-between mb-3">
          <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] font-bold text-navy/55">Funding amount</div>
          <div className="font-display font-extrabold text-[32px] tracking-[-0.03em] text-navy leading-none" data-testid="calc-amount-display">
            {formatGBPCompact(amount)}
          </div>
        </div>
        <Slider
          data-testid="calc-amount-slider"
          value={[amountToPos(amount)]} min={0} max={SLIDER_STEPS} step={1}
          onValueChange={(v) => setAmount(posToAmount(v[0]))}
          className="[&>span]:bg-navy/15 [&_span[data-orientation=horizontal]>span]:bg-emerald"
        />
        <div className="flex justify-between font-mono text-[10px] text-navy/40 mt-2 font-semibold">
          <span>£5k</span><span>£500k</span><span>£10M</span><span>£500M</span>
        </div>

        <div className="h-px bg-navy/10 my-4" />

        <div className="flex items-baseline justify-between mb-3">
          <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] font-bold text-navy/55">Repay over</div>
          <div className="font-display font-extrabold text-[20px] tracking-[-0.02em] text-navy" data-testid="calc-term-display">
            {term} {term === 1 ? "month" : "months"}
          </div>
        </div>
        <Slider
          data-testid="calc-term-slider"
          value={[term]} min={1} max={120} step={1}
          onValueChange={(v) => setTerm(v[0])}
          className="[&>span]:bg-navy/15 [&_span[data-orientation=horizontal]>span]:bg-emerald"
        />
        <div className="flex justify-between font-mono text-[10px] text-navy/40 mt-2 font-semibold">
          <span>1m</span><span>36m</span><span>60m</span><span>120m</span>
        </div>
      </div>

      {/* Cost strip */}
      <div className="grid grid-cols-2 gap-0 rounded-xl overflow-hidden border border-line mb-6">
        <div className="px-4 py-3 bg-white border-r border-line">
          <div className="font-mono text-[9.5px] uppercase tracking-[0.14em] font-bold text-navy/50 mb-1">Indicative monthly</div>
          <div className="font-display font-extrabold text-[18px] tracking-[-0.02em] text-navy">{formatGBP2(monthly)}</div>
        </div>
        <div className="px-4 py-3 bg-sage/40">
          <div className="font-mono text-[9.5px] uppercase tracking-[0.14em] font-bold text-navy/50 mb-1">From APR</div>
          <div className="font-display font-extrabold text-[18px] tracking-[-0.02em] text-emerald-2">{(DEFAULT_INDICATIVE_APR * 100).toFixed(1)}%</div>
        </div>
      </div>

      {/* CTA */}
      <button
        onClick={submit}
        data-testid="calc-apply-btn"
        className="btn-shine group w-full h-[54px] rounded-xl bg-emerald text-white font-extrabold hover:bg-emerald-2 transition-colors flex items-center justify-center gap-2 text-[15px] shadow-emerald"
      >
        Get My Tailored Quote
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.4} />
      </button>

      {/* Social proof bar */}
      <div className="mt-5 flex items-center justify-center gap-2 text-[12px] font-semibold text-navy/65">
        <Users className="h-3.5 w-3.5 text-emerald" strokeWidth={2.4} />
        <span><b className="text-emerald-2">12</b> businesses started an application in the last hour</span>
      </div>
      <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-navy/50 font-medium">
        <ShieldCheck className="h-3 w-3" strokeWidth={2.4} />
        Soft check only — no footprint on the business credit file
      </div>
    </div>
  );
}
